import React, { useState, useEffect, useCallback } from "react";

export default function HistoryPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const limit = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/logs?page=${page}&limit=${limit}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setLogs(data.logs);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error("Failed to fetch logs:", err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatDateRange = (dateStr, durationMs) => {
    const start = new Date(dateStr);
    const datePart = start.toLocaleDateString();
    const startTime = start.toLocaleTimeString();
    if (durationMs == null) return `${datePart} ${startTime}`;
    const end = new Date(start.getTime() + durationMs);
    const endTime = end.toLocaleTimeString();
    return `${datePart} ${startTime} – ${endTime} (${(durationMs / 1000).toFixed(1)}s)`;
  };

  const truncate = (str, len = 100) => {
    if (!str) return "—";
    return str.length > len ? str.slice(0, len) + "..." : str;
  };

  return (
    <div className="logs-container">
      <div className="logs-header">
        <h2>Request History</h2>
        <div className="logs-header-right">
          <span className="logs-total">{total} total requests</span>
          <button className="pagination-btn" onClick={fetchLogs} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="logs-loading">Loading...</div>
      ) : logs.length === 0 ? (
        <div className="logs-empty">No requests logged yet.</div>
      ) : (
        <>
          <table className="logs-table">
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>IP Address</th>
                <th>Token</th>
                <th>Prompt</th>
                <th>Status</th>
                <th>Stream</th>
                <th>Proposal</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr
                    className={`logs-row ${expandedId === log.id ? "expanded" : ""}`}
                    onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                  >
                    <td>{formatDateRange(log.created_at, log.duration_ms)}</td>
                    <td>{log.ip_address}</td>
                    <td className="token-cell">{log.api_token_used}</td>
                    <td className="prompt-preview">{truncate(log.prompt)}</td>
                    <td>
                      <span className={`status-badge ${log.status_code === 200 ? "status-200" : "status-error"}`}>
                        {log.status_code}
                      </span>
                    </td>
                    <td>{log.is_streaming ? "Yes" : "No"}</td>
                    <td>{log.is_proposal ? "Yes" : "No"}</td>
                  </tr>
                  {expandedId === log.id && (
                    <tr className="expanded-detail-row">
                      <td colSpan="7">
                        <div className="expanded-content">
                          <div className="expanded-section">
                            <strong>Full Prompt:</strong>
                            <pre>{log.prompt}</pre>
                          </div>
                          <div className="expanded-section">
                            <strong>Response:</strong>
                            <pre>{log.response || "—"}</pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {page} of {totalPages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
